import { useState, useEffect, useRef } from "react";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import MathLiveFormula from "../MathLiveFormula";
import parseStringFromHtml from "../utils/parseStringFromHtml";

const useMathEditor = ({ value, onChange, onKeyDown }) => {
  const isFocusedRef = useRef();
  const mathNodeRef = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [caretPosition, setCaretPosition] = useState();

  const handleUpdate = (html) => {
    onChange(parseStringFromHtml(html));
  };

  const handleCaretMove = ({ editor }) => {
    if (!isFocusedRef.current) return false;
    const { selection } = editor.state;
    const { from, to } = selection;
    const { top, left } = editor.view.coordsAtPos(from);
    const resolvedPos = editor.view.state.doc.resolve(from);

    // Get the node at the caret position
    const node = resolvedPos.nodeAfter;

    if (node && node.type.name === "mathliveFormula" && from !== to) {
      // Find the MathLive node in the DOM
      const mathNode = editor.view.nodeDOM(from);

      if (mathNode) {
        // Add a highlight class
        mathNode.classList.add("mathlive-highlight");
        mathNodeRef.current = mathNode;
      }
    } else {
      // Remove highlight if caret moves away
      document.querySelectorAll(".mathlive-highlight").forEach((el) => {
        el.classList.remove("mathlive-highlight");
      });
      mathNodeRef.current = null;
    }

    setCaretPosition({
      top: top - 6,
      left,
    });
  };

  const handleFocus = ({ editor }) => {
    isFocusedRef.current = true;
    handleCaretMove({ editor });
  };

  const handleBlur = () => {
    isFocusedRef.current = false;
  };

  const editor = useEditor({
    extensions: [StarterKit, MathLiveFormula],
    content: "",
    onUpdate: ({ editor }) => {
      handleUpdate(editor.getHTML());
    },
    onFocus: handleFocus,
    onBlur: handleBlur,
    onSelectionUpdate: handleCaretMove,
    editorProps: {
      handleKeyDown: (view, event) => {
        if (
          (event.ctrlKey || event.metaKey || event.altKey) &&
          event.code === "KeyM"
        ) {
          event.preventDefault();
          setIsOpen(true);
          return true;
        } else if (
          mathNodeRef.current &&
          mathNodeRef.current?.nodeName === "MATH-FIELD" &&
          event.key === "Enter"
        ) {
          event.preventDefault();
          setIsOpen(true);
          return true;
        }
        return onKeyDown(event); // return true tells TipTap we already handled the event
      },
    },
  });

  const insertFormula = (submittedLatex) => {
    if (!editor || !submittedLatex) return;

    editor
      .chain()
      .focus()
      .insertContent({
        type: "mathliveFormula",
        attrs: {
          latex: submittedLatex,
        },
      })
      .run();
  };

  const editorAttribs = {
    editor,
  };

  const popupAttribs = {
    isOpen,
    onToggle: setIsOpen,
  };

  const mathLiveAttribs = {
    onSubmit: insertFormula,
    value:
      mathNodeRef.current?.nodeName === "MATH-FIELD"
        ? mathNodeRef.current?.value
        : null,
    closePopup: () => {
      setIsOpen(false);
    },
  };

  useEffect(() => {
    if (value === "") {
      if (editor) {
        editor.commands.clearContent(true);
      }
    }
    // eslint-disable-next-line
  }, [value]);

  useEffect(() => {
    const handleResize = () => {
      if (editor) {
        handleCaretMove({ editor });
      }
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
    // eslint-disable-next-line
  }, []);

  return {
    editor,
    insertFormula,
    editorAttribs,
    popupAttribs,
    mathLiveAttribs,
    caretPosition,
  };
};

export default useMathEditor;
